import { Avatar, Box, styled, Theme } from '@mui/material';
import { palette } from 'view/themes/palette';

export const Wrapper = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100vh',
    height: 'auto',
    width: '100%',
    gap: '24px',
    backgroundColor: palette.background.general,

    [theme.breakpoints.down('tablet')]: {
      padding: 0,
      backgroundColor: palette.white,
      gap: '0px',
    },
  };
});

export const MainContentWrapper = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    width: '80%',
    paddingBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',

    [theme.breakpoints.down('desktop')]: {
      width: '95%',
    },

    [theme.breakpoints.down('tablet')]: {
      width: '100%',
      padding: '0 16px 32px 16px',
    },
  };
});

export const BackButtonBox = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    display: 'flex',
    alignItems: 'center',

    [theme.breakpoints.down('tablet')]: {
      paddingTop: '16px',
    },
  };
});

export const Card = styled(Box)({
  backgroundColor: palette.white,
  borderRadius: '24px',
  padding: '24px',
});

export const CardTopRow = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',

    [theme.breakpoints.down('tablet')]: {
      position: 'relative',
    },
  };
});

export const CompanyHeaderRow = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    display: 'flex',
    gap: '24px',
    alignItems: 'flex-start',
    flex: 1,

    [theme.breakpoints.down('tablet')]: {
      flexDirection: 'column',
    },
  };
});

export const HeaderInfo = styled(Box)({
  flex: 1,
});

// Purple is the monogram-fallback background only; with a real logo we use a light neutral so a
// transparent PNG doesn't sit on purple.
export const Logo = styled(Avatar, {
  shouldForwardProp: (prop) => {
    return prop !== 'hasImage';
  },
})<{ hasImage?: boolean }>(({ hasImage }) => {
  return {
    width: '96px',
    height: '96px',
    fontSize: '36px',
    fontWeight: 600,
    borderRadius: '12px',
    border: `1px solid ${palette.border.default}`,
    backgroundColor: hasImage ? palette.background.light : palette.primary.purple500,
    color: palette.white,
    // Small gap between the border and the logo image (no padding for the monogram fallback).
    '& img': {
      objectFit: 'contain',
      padding: hasImage ? '10px' : 0,
    },
  };
});

export const CompanyName = styled('h1')({
  margin: '0 0 8px 0',
  fontSize: '28px',
  fontWeight: 700,
  color: palette.text.header,
  lineHeight: 1.3,
});

export const ShortDescription = styled('p')({
  margin: '0 0 16px 0',
  fontSize: '15px',
  color: '#6B7280',
  lineHeight: 1.5,
});

export const MetaRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  alignItems: 'center',
  fontSize: '14px',
  color: '#6B7280',
});

export const MetaItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',

  '& svg': {
    fontSize: '18px',
  },
});

// Project tag/pill style (matches CandidateProfile `ReusableTextBlockDesc`).
export const Tag = styled(Box)({
  width: 'max-content',
  color: palette.text.header,
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '136%',
  padding: '12px 16px',
  borderRadius: '24px',
  backgroundColor: palette.background.blue500,
});

export const WebsiteLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: palette.primary.purple500,
  textDecoration: 'none',
  fontWeight: 500,

  '&:hover': {
    textDecoration: 'underline',
  },

  '& svg': {
    fontSize: '18px',
  },
});

export const EditButtonDesktop = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    flexShrink: 0,

    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  };
});

export const EditButtonMobile = styled(Box)(({ theme }: { theme: Theme }) => {
  return {
    display: 'none',

    [theme.breakpoints.down('tablet')]: {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '36px',
      height: '36px',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      border: `1px solid ${palette.border.default}`,
      cursor: 'pointer',
      color: '#6B7280',
      transition: 'all 0.2s',

      '&:hover': {
        borderColor: palette.primary.purple500,
        color: palette.primary.purple500,
      },
    },
  };
});

export const AboutSection = styled(Box)({
  marginTop: '24px',
  paddingTop: '24px',
  borderTop: `1px solid ${palette.border.default}`,
});

export const SectionTitle = styled('h2')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '0 0 16px 0',
  fontSize: '18px',
  fontWeight: 600,
  color: palette.text.header,

  '& svg': {
    color: palette.primary.purple500,
  },
});

export const AboutText = styled('p')({
  margin: '0 0 8px 0',
  fontSize: '15px',
  color: palette.text.body,
  lineHeight: 1.6,

  '&:last-of-type': {
    marginBottom: 0,
  },
});

export const JobList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const JobCard = styled(Box)({
  padding: '20px 24px',
  backgroundColor: palette.white,
  borderRadius: '12px',
  border: `1px solid ${palette.border.default}`,
  cursor: 'pointer',
  transition: 'all 0.2s',

  '&:hover': {
    boxShadow: '0 8px 16px rgba(102, 52, 151, 0.15)',
    borderColor: palette.primary.purple200,
    transform: 'translateY(-2px)',
  },
});

export const JobTitle = styled('h3')({
  margin: '0 0 12px 0',
  fontSize: '18px',
  fontWeight: 600,
  color: palette.text.header,
});

export const JobMeta = styled(Box)({
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap',
  alignItems: 'center',
  fontSize: '14px',
  color: '#6B7280',
  marginBottom: '8px',
});

export const JobSalary = styled('div')({
  fontSize: '14px',
  color: '#6B7280',
});

export const JobDescription = styled('p')({
  margin: '8px 0 0 0',
  fontSize: '14px',
  color: '#6B7280',
  lineHeight: 1.5,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

export const EmptyText = styled('p')({
  margin: 0,
  fontSize: '15px',
  color: '#6B7280',
});

export const Attribution = styled('p')({
  margin: '8px 0 24px 0',
  textAlign: 'center',
  fontSize: '13px',
  color: '#9CA3AF',
});

export const CenteredLoader = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 'calc(100vh - 200px)',
});

export const ErrorText = styled('p')({
  textAlign: 'center',
  fontSize: '16px',
  color: palette.alerts.error,
});
